<template>
    <video ref="videoRef" class="block w-full h-full object-cover bg-black" autoplay playsinline muted />
</template>

<script setup lang="ts">
    import { onBeforeUnmount, onMounted, ref } from 'vue'
    import { useSettingsStore } from '@/stores/settings'
    import { startScanner } from '@/services/scanner/detector'
    import type { ScannerHandle } from '@/services/scanner/types'

    const props = defineProps<{
        onDetected: (code: string) => void
    }>()

    const settings = useSettingsStore()
    const videoRef = ref<HTMLVideoElement | null>(null)
    let handle: ScannerHandle | null = null
    // startScanner is async; if the component unmounts before it resolves (e.g.
    // a fast navigate/remount during the deep-link flow), onBeforeUnmount runs
    // while handle is still null and can't stop anything. The late-arriving
    // handle would then leave an orphaned camera MediaStream running, which
    // blocks the next scanner mount from acquiring the camera (black preview).
    // The `cancelled` flag lets us stop that late handle immediately.
    let cancelled = false

    onMounted(async () => {
        if (!videoRef.value) return
        try {
            const h = await startScanner({
                videoElement: videoRef.value,
                cameraId: settings.camera,
                onDetected: props.onDetected
            })
            if (cancelled) {
                // Unmounted while starting — release the camera we just acquired.
                void h.stop()
                return
            }
            handle = h
        } catch (err) {
            console.error('scanner start failed', err)
        }
    })

    onBeforeUnmount(() => {
        cancelled = true
        void handle?.stop()
        handle = null
    })
</script>
